/**
 * Runtime backend URL resolution.
 *
 * Priority:
 *   1. VITE_API_URL env var — set to the StreamPersona customer-facing CNAME
 *      (e.g. https://live.streampersona.app), which resolves to the
 *      permanent static ngrok subdomain backing the GPU container
 *   2. localhost:8000 — dev fallback
 *
 * The frontend is hardwired to live.streampersona.app — the customer-facing
 * CNAME that tunnels to the permanent static ngrok subdomain — eliminating
 * any runtime URL discovery or Edge Config lookups.
 */

export const GATEWAY_URL = (import.meta.env.VITE_GATEWAY_URL || "https://api.streampersona.app").replace(/\/+$/, "");

export type AppEdition = "lite" | "studio";
export const APP_EDITION: AppEdition = (import.meta.env.VITE_APP_EDITION as AppEdition) || "lite";
export const isLiteEdition = (): boolean => APP_EDITION === "lite";
export const isStudioEdition = (): boolean => APP_EDITION === "studio";

let _resolvedBase = '';
let _resolvedWs = '';

export async function resolveBackendUrl(): Promise<string> {
  // Fallback to build-time env var, then localhost
  _resolvedBase = _resolvedBase || (import.meta.env.VITE_API_URL ?? 'http://localhost:8000');
  _resolvedWs = _resolvedBase.replace(/^https?/, (m: string) => (m === 'https' ? 'wss' : 'ws'));
  return _resolvedBase;
}

export function setDynamicBackendUrl(url: string): void {
  const cleanUrl = url.replace(/\/+$/, "");
  _resolvedBase = cleanUrl;
  _resolvedWs = cleanUrl.replace(/^https?/, (m: string) => (m === 'https' ? 'wss' : 'ws'));
}

export function getApiBase(): string {
  return _resolvedBase || (import.meta.env.VITE_API_URL ?? 'http://localhost:8000');
}

export function getWsBase(): string {
  if (_resolvedWs) return _resolvedWs;
  const base = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
  return base.replace(/^https?/, (m: string) => (m === 'https' ? 'wss' : 'ws'));
}

/**
 * Standard fetch wrapper for API requests.
 * Use this instead of raw fetch for all API calls.
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${getApiBase()}${path}`, init);
}

/**
 * Structured frontend audit logger that reports operational events to the backend.
 * Appended to the central audit.log file on the server.
 */
export async function auditLog(eventType: string, metadata: Record<string, unknown> = {}): Promise<void> {
  try {
    await fetch(`${getApiBase()}/api/system/audit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type: eventType, metadata }),
    });
  } catch (e) {
    console.error("Failed to submit frontend audit event:", e);
  }
}
